<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="80%"
    :close-on-click-modal="false"
    :append-to-body="true"
    :modal="true"
    :modal-append-to-body="true"
    :show-close="false"
    @close="handleClose"
    class="custom-card-dialog"
  >
    <!-- Custom header with title and buttons -->
    <div slot="title" class="dialog-header">
      <span class="dialog-title">{{ isEditing ? 'Edit Card' : 'Add New Card' }}</span>
      <div class="dialog-header-buttons">
        <el-button size="small" @click="handleClose">Cancel</el-button>
        <div v-if="!isEditing" class="create-button-wrapper">
          <el-dropdown trigger="hover" @command="handleCreateDropdown">
            <el-button
              size="small"
              type="primary"
              class="create-button"
              @click="onCreateClick"
              :loading="saving || savingAndSummarizing"
              :disabled="saving || savingAndSummarizing"
            >
              Create
              <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="enhance">Create and Enhance</el-dropdown-item>
              <el-dropdown-item command="summarize">Create and Summarize</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <div
          v-else
          class="update-button-wrapper"
          @mouseenter="showEnhanceButton = true"
          @mouseleave="showEnhanceButton = false"
        >
          <el-button
            size="small"
            type="primary"
            @click.stop.prevent="handleSave(false, false, true)"
            :loading="saving"
            :disabled="saving || savingWithEnhancement"
            filterable
            :reserve-keyword="true"
          >
            Update
          </el-button>
          <div
            v-if="!saving && !savingWithEnhancement"
            class="enhance-button-dropdown"
          >
            <el-button
              v-show="showEnhanceButton"
              size="small"
              type="success"
              @click.stop.prevent="handleSaveWithEnhancement"
              :loading="savingWithEnhancement"
              :disabled="saving || savingWithEnhancement"
              class="enhance-button"
            >
              Update with AI Enhancement
            </el-button>
          </div>
        </div>
      </div>
    </div>
    <el-form :model="cardForm" :rules="cardRules" ref="cardForm" label-width="0px" class="no-label-form">
      <!-- Title input for editing mode -->
      <el-form-item v-if="isEditing" prop="title" class="form-item-no-label">
        <el-input v-model="cardForm.title" placeholder="Enter card title (AI will generate if left blank)..."
          maxlength="100" show-word-limit size="large"></el-input>
      </el-form-item>

      <!-- Title input for creating mode - shown only when showTitleInput is true -->
      <el-form-item v-if="!isEditing && showTitleInput" prop="title" class="form-item-no-label">
        <el-input v-model="cardForm.title" placeholder="Enter card title (AI will generate if left blank)..."
          maxlength="100" show-word-limit size="large"></el-input>
      </el-form-item>

      <!-- Add Title Manually button - shown only when creating and title input is hidden -->
      <div v-if="!isEditing && !showTitleInput" class="add-title-button-wrapper">
        <el-button size="small" @click="showTitleInput = true" plain>Add Title Manually</el-button>
      </div>

      <el-form-item prop="content_type" class="form-item-no-label content-type-row">
        <div style="display: flex; gap: 20px; align-items: center;">
          <el-radio-group v-model="cardForm.content_type" @change="handleContentTypeChangeWrapper" size="medium">
            <el-radio-button label="text">Text</el-radio-button>
            <el-radio-button label="image">Image</el-radio-button>
            <el-radio-button label="video">Video</el-radio-button>
            <el-radio-button label="quiz">Quiz</el-radio-button>
          </el-radio-group>

          <!-- Editor Type Selector - Show only when Text is selected -->
          <el-radio-group v-if="cardForm.content_type === 'text'" v-model="editorType" size="medium">
            <el-radio-button label="plain">Plain Text</el-radio-button>
            <el-radio-button label="markdown">Markdown</el-radio-button>
            <el-radio-button label="richtext">Rich Text</el-radio-button>
          </el-radio-group>
        </div>
      </el-form-item>


      <!-- Text Content -->
      <template v-if="cardForm.content_type === 'text'">

        <!-- Plain Text Editor -->
        <el-form-item v-if="editorType === 'plain'" prop="content" class="content-textarea-item form-item-no-label">
          <el-input
            type="textarea"
            v-model="cardForm.content"
            placeholder="Enter your card content or use voice recording..."
            :autosize="{ minRows: 10, maxRows: 20 }"
            maxlength="5000"
            show-word-limit
          ></el-input>
          <!-- Audio Recording Button - below textarea, same row as word count -->
          <div class="audio-recording-inline">
            <Translate
              :needTranslate="false"
              @addText="addTextToContent"
              startBtnText="Start recording"
              endBtnText="Stop recording"
              :showAudioWaveform="false"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
        </el-form-item>

        <!-- Markdown Editor -->
        <el-form-item v-else-if="editorType === 'markdown'" prop="content" class="markdown-editor-item form-item-no-label">
          <mavon-editor
            ref="mavonEditor"
            v-model="cardForm.content"
            @imgAdd="handleMavonImageAdd"
            :toolbars="{
              bold: true,
              italic: true,
              header: true,
              underline: true,
              strikethrough: true,
              mark: true,
              superscript: true,
              subscript: true,
              quote: true,
              ol: true,
              ul: true,
              link: true,
              imagelink: false,
              code: true,
              table: true,
              fullscreen: true,
              readmodel: true,
              htmlcode: false,
              help: true,
              undo: true,
              redo: true,
              trash: true,
              save: false,
              navigation: false,
              alignleft: true,
              aligncenter: true,
              alignright: true,
              subfield: true,
              preview: true
            }"
            :style="{minHeight: '400px', maxHeight: '600px'}"
            placeholder="Enter your card content in Markdown..."
          />
          <div class="editor-tip">Supports Markdown syntax: **bold**, *italic*, # headers, - lists, etc.</div>
          <!-- Audio Recording Button - below editor -->
          <div class="audio-recording-inline">
            <Translate
              :needTranslate="false"
              @addText="addTextToContent"
              startBtnText="Start recording"
              endBtnText="Stop recording"
              :showAudioWaveform="false"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
        </el-form-item>

        <!-- Rich Text Editor -->
        <el-form-item v-else-if="editorType === 'richtext'" prop="content" class="richtext-editor-item form-item-no-label">
          <quill-editor
            v-model="cardForm.content"
            :options="quillOptions"
            :style="{minHeight: '400px', maxHeight: '600px'}"
          />
          <div class="editor-tip">Use the toolbar above to format your text</div>
          <!-- Audio Recording Button - below editor -->
          <div class="audio-recording-inline">
            <Translate
              :needTranslate="false"
              @addText="addTextToContent"
              startBtnText="Start recording"
              endBtnText="Stop recording"
              :showAudioWaveform="false"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
        </el-form-item>
      </template>

      <!-- Image Content -->
      <template v-else-if="cardForm.content_type === 'image'">
        <el-form-item prop="fileList" class="form-item-no-label">
          <div class="image-upload-container" @click="triggerFileInput">
            <input ref="fileInput" type="file" @change="handleImageChange" accept="image/*" style="display: none;" />
            <div class="upload-area" :class="{ 'has-image': imageUrl }">
              <img v-if="imageUrl" :src="imageUrl" class="uploaded-image" alt="Uploaded image">
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus upload-icon"></i>
                <p class="upload-text">Click to upload image</p>
              </div>
            </div>
          </div>
          <div class="upload-tip">Only jpg/png files are allowed, and size should be less than 5MB</div>
        </el-form-item>
        <div v-if="cardForm.image_description" style="margin-bottom: 20px;">
          <div class="image-description-item form-item-no-label">
            <div class="image-description-title">Image Description</div>
            <div class="image-description-content">{{ cardForm.image_description }}</div>
          </div>

        </div>
      </template>

      <!-- Video Content -->
      <template v-else-if="cardForm.content_type === 'video'">
        <el-form-item prop="fileList" class="form-item-no-label">
          <el-upload class="video-uploader" :action="uploadUrl" :headers="uploadHeaders" :data="uploadData"
            :show-file-list="false" :on-success="handleVideoSuccess" :before-upload="beforeVideoUpload"
            accept="video/*">
            <video v-if="videoUrl" :src="videoUrl" controls class="uploaded-video"></video>
            <div v-else class="upload-placeholder">
              <i class="el-icon-video-camera"></i>
              <span>Click to upload video</span>
            </div>
          </el-upload>
          <div class="upload-tip">Only mp4/mov files are allowed, and size should be less than 50MB</div>
        </el-form-item>
      </template>

      <!-- Quiz Content -->
      <template v-else-if="cardForm.content_type === 'quiz'">
        <el-form-item prop="quiz_content.question" class="form-item-no-label">
          <el-input
            type="textarea"
            v-model="cardForm.quiz_content.question"
            placeholder="Enter your quiz question or use voice recording..."
            :autosize="{ minRows: 3, maxRows: 6 }"
            maxlength="500"
            show-word-limit
          ></el-input>
          <!-- Audio Recording Button for Quiz Question - below textarea -->
          <div class="audio-recording-inline">
            <Translate
              :needTranslate="false"
              @addText="addTextToQuizQuestion"
              startBtnText="Start recording"
              endBtnText="Stop recording"
              :showAudioWaveform="false"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
        </el-form-item>

        <el-form-item prop="quiz_content.options" class="form-item-no-label">
          <div v-for="(option, index) in cardForm.quiz_content.options" :key="index" class="quiz-option-input">
            <el-input v-model="cardForm.quiz_content.options[index]"
              :placeholder="`Option ${['A', 'B', 'C', 'D'][index]}`" maxlength="200">
              <template slot="prepend">{{ ['A', 'B', 'C', 'D'][index] }}.</template>
            </el-input>
          </div>
        </el-form-item>

        <el-form-item prop="quiz_content.explanation" class="form-item-no-label">
          <el-input type="textarea" v-model="cardForm.quiz_content.explanation"
            placeholder="Explain the correct answer..." :autosize="{ minRows: 4, maxRows: 8 }" maxlength="1000"
            show-word-limit></el-input>
        </el-form-item>
      </template>

      <!-- Resources Section -->
      <el-form-item prop="resources" class="form-item-no-label resources-section">
        <div class="resources-header">
          <div class="resources-title-wrapper">
            <span class="resources-title">Resources</span>
            <span v-if="cardForm.resources.length === 0" class="resources-hint">
              (optional - add references for this card)
            </span>
          </div>
          <el-button 
            size="mini" 
            type="primary" 
            icon="el-icon-plus" 
            @click="addResource"
            plain
          >
            Add Resource
          </el-button>
        </div>
        
        <div v-if="cardForm.resources.length > 0" class="resources-list">
          <div 
            v-for="(resource, index) in cardForm.resources" 
            :key="index" 
            class="resource-item"
            :class="{ 'expanded': resourcesExpanded[index] }"
          >
            <!-- Quick Input Mode (Collapsed) -->
            <div class="resource-quick-input">
              <el-input
                v-model="resource.name"
                placeholder="Resource name (e.g., Book title, article name, website...)"
                size="medium"
                class="resource-name-input"
              >
                <template slot="prepend">{{ index + 1 }}.</template>
              </el-input>
              
              <div class="resource-actions">
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-arrow-down"
                  @click="toggleResourceExpand(index)"
                  v-if="!resourcesExpanded[index]"
                  title="Show more options"
                >
                </el-button>
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-arrow-up"
                  @click="toggleResourceExpand(index)"
                  v-else
                  title="Show less"
                >
                </el-button>
                <el-button
                  size="mini"
                  type="text"
                  icon="el-icon-delete"
                  @click="removeResource(index)"
                  title="Remove resource"
                >
                </el-button>
              </div>
            </div>
            
            <!-- Expanded Details -->
            <transition name="slide-down">
              <div v-if="resourcesExpanded[index]" class="resource-details">
                <el-row :gutter="10">
                  <el-col :span="12">
                    <el-input
                      v-model="resource.url"
                      placeholder="URL/Link (optional)"
                      size="small"
                    >
                      <template slot="prepend">URL</template>
                    </el-input>
                  </el-col>
                  <el-col :span="12">
                    <el-select
                      v-model="resource.type"
                      placeholder="Type"
                      size="small"
                      style="width: 100%"
                    >
                      <el-option label="Book" value="book"></el-option>
                      <el-option label="Article" value="article"></el-option>
                      <el-option label="Video" value="video"></el-option>
                      <el-option label="Website" value="website"></el-option>
                      <el-option label="Course" value="course"></el-option>
                      <el-option label="Paper" value="paper"></el-option>
                      <el-option label="Other" value="other"></el-option>
                    </el-select>
                  </el-col>
                </el-row>
                
                <el-row :gutter="10" style="margin-top: 10px;">
                  <el-col :span="8">
                    <el-input
                      v-model="resource.author"
                      placeholder="Author/Creator (optional)"
                      size="small"
                    >
                      <template slot="prepend">Author</template>
                    </el-input>
                  </el-col>
                  <el-col :span="8">
                    <el-input
                      v-model="resource.major"
                      placeholder="Major/Field (optional)"
                      size="small"
                    >
                      <template slot="prepend">Major</template>
                    </el-input>
                  </el-col>
                  <el-col :span="8">
                    <el-input
                      v-model="resource.subject"
                      placeholder="Subject/Topic (optional)"
                      size="small"
                    >
                      <template slot="prepend">Subject</template>
                    </el-input>
                  </el-col>
                </el-row>
                
                <el-row :gutter="10" style="margin-top: 10px;">
                  <el-col :span="24">
                    <el-input
                      type="textarea"
                      v-model="resource.description"
                      placeholder="Additional notes or description (optional)"
                      :autosize="{ minRows: 2, maxRows: 4 }"
                      size="small"
                    >
                    </el-input>
                  </el-col>
                </el-row>
              </div>
            </transition>
          </div>
        </div>
      </el-form-item>

      <!-- Collection Selection -->
      <el-form-item prop="collection_ids" class="form-item-no-label">
        <el-select
          v-model="cardForm.collection_ids"
          placeholder="Select collections (optional)"
          size="large"
          multiple
          :collapse-tags="cardForm.collection_ids && cardForm.collection_ids.length > 5"
          clearable
          style="width: 100%"
          @focus="handleCollectionFocus"
          @visible-change="handleCollectionDropdownVisible"
        >
          <el-option
            v-for="collection in collections"
            :key="collection.id"
            :label="collection.name"
            :value="collection.id"
          >
            <span style="float: left">{{ collection.name }}</span>
            <span style="float: right; color: #8492a6; font-size: 13px">
              {{ collection.card_count || 0 }} cards
            </span>
          </el-option>
        </el-select>
      </el-form-item>

      <!-- Reminder Settings - Only show when editing -->
      <el-form-item v-if="isEditing" class="form-item-no-label reminder-item">
        <el-tooltip
          content="Enable standard reminders for this card (3 days, 1 week, 1 month, 6 months, 1 year, 2 years, 4 years)"
          placement="top">
          <i class="el-icon-info reminder-info-icon"></i>
        </el-tooltip>
        <el-switch v-model="cardForm.enable_reminder" active-text="Enable Reminders" inactive-text="No Reminders">
        </el-switch>
      </el-form-item>
    </el-form>

    <!-- Reminder Confirmation Dialog - shown after creating new card -->
    <el-dialog
      title="Enable Reminders?"
      :visible.sync="showReminderDialog"
      width="450px"
      :append-to-body="true"
      :close-on-click-modal="false"
    >
      <div style="padding: 20px 0;">
        <p style="margin-bottom: 20px; font-size: 15px; line-height: 1.6;">
          Would you like to enable standard reminders for this card?
        </p>
        <p style="color: #909399; font-size: 13px; line-height: 1.6;">
          Standard reminders will notify you at: 3 days, 1 week, 1 month, 6 months, 1 year, 2 years, and 4 years after creation.
        </p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="handleReminderChoice(false)">No Thanks</el-button>
        <el-button type="primary" @click="handleReminderChoice(true)">Enable Reminders</el-button>
      </div>
    </el-dialog>

    <!-- Remove the footer slot since buttons are in header -->
  </el-dialog>
</template>

<script>
import * as qiniu from "qiniu-js";
import { postRequest,sendConversation } from "@/apis/front";
import { mavonEditor } from 'mavon-editor';
import 'mavon-editor/dist/css/index.css';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import Translate from "@/components/Translate/index.vue";

export default {
  name: 'EditCard',
  components: {
    mavonEditor,
    quillEditor,
    Translate
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    card: {
      type: Object,
      default: () => null
    },
    collections: {
      type: Array,
      default: () => []
    },
    preselectedCollectionId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      dialogVisible: false,
      saving: false,
      savingAndSummarizing: false,
      savingWithEnhancement: false,
      showEnhanceButton: false,
      showTitleInput: false, // Show/hide title input for new cards
      showReminderDialog: false, // Show reminder confirmation after creating new card
      createdCardData: null, // Store the newly created card data
      // Removed hover-based secondary button logic
      imageUrl: '',
      videoUrl: '',
      editorType: 'plain', // 'plain', 'markdown', or 'richtext'
      originalContent: null, // Store original content for comparison
      originalQuizContent: null, // Store original quiz content for comparison
      // Store content for each type to preserve when switching
      contentStore: {
        text: '',
        image: { url: '', fileList: [] },
        video: { url: '', fileList: [] },
        quiz: {
          question: '',
          options: ['', '', '', ''],
          explanation: ''
        }
      },
      previousContentType: null, // Track previous content type for preservation
      quillOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        },
        placeholder: 'Enter your card content...'
      },
      cardForm: {
        id: '',
        title: '',
        content_type: 'text',
        content: '',
        fileList: [],
        quiz_content: {
          question: '',
          options: ['', '', '', ''],
          explanation: ''
        },
        labels: [],
        enable_reminder: true,
        collection_ids: [],
        resources: []
      },
      resourcesExpanded: [], // Track which resources are expanded
      cardRules: {
        content_type: [
          { required: true, message: 'Please select content type', trigger: 'change' }
        ],
        content: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (this.cardForm.content_type === 'text' && (!value || value.trim() === '')) {
                callback(new Error('Please enter content'));
              } else {
                callback();
              }
            },
            trigger: 'blur'
          },
          { min: 1, max: 5000, message: 'Content length should be 1 to 5000 characters', trigger: 'blur' }
        ],
        fileList: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if ((this.cardForm.content_type === 'image' || this.cardForm.content_type === 'video') &&
                (!value || value.length === 0)) {
                callback(new Error(`Please upload a ${this.cardForm.content_type}`));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        'quiz_content.question': [
          { required: true, message: 'Please enter quiz question', trigger: 'blur' },
          { min: 5, max: 500, message: 'Question length should be 5 to 500 characters', trigger: 'blur' }
        ],
        'quiz_content.explanation': [
          { required: true, message: 'Please enter explanation', trigger: 'blur' },
          { min: 5, max: 1000, message: 'Explanation length should be 5 to 1000 characters', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isEditing() {
      return this.card && this.card.id
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        this.initializeForm()
      }
    },
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  methods: {
    handleCollectionFocus(event) {
      // Prevent accidental select-all in the dropdown input
      try {
        event && event.stopPropagation && event.stopPropagation()
        // Clear any global text selection
        if (window && window.getSelection) {
          const sel = window.getSelection()
          if (sel && sel.removeAllRanges) sel.removeAllRanges()
        }
        // Move caret to end without selecting text
        const input = this.$el.querySelector('.el-select .el-input__inner')
        if (input) {
          const len = input.value ? input.value.length : 0
          if (typeof input.setSelectionRange === 'function') {
            input.setSelectionRange(len, len)
          }
        }
      } catch (e) {
        // no-op
      }
    },
    handleCollectionDropdownVisible(visible) {
      // When dropdown opens, ensure input isn't auto-selected
      if (visible) {
        this.$nextTick(() => {
          try {
            const input = this.$el.querySelector('.el-select .el-input__inner')
            if (input) {
              // Do not blur (keeps keyboard nav), just clear selection and place caret at end
              const len = input.value ? input.value.length : 0
              if (typeof input.setSelectionRange === 'function') {
                input.setSelectionRange(len, len)
              }
              if (window && window.getSelection) {
                const sel = window.getSelection()
                if (sel && sel.removeAllRanges) sel.removeAllRanges()
              }
            }
          } catch (e) {
            // no-op
          }
        })
      }
    },
    handleCreateDropdown(cmd) {
      if (cmd === 'enhance') {
        // Create and Enhance
        this.handleSave(false)
      } else if (cmd === 'summarize') {
        // Create and Summarize
        this.handleSave(true)
      }
    },
    handleCreateCommand(cmd) {
      if (cmd === 'create_and_summarize') {
        this.onCreateSummarizeClick()
      }
    },
    onCreateClick() {
      // Primitive create without AI enhancements
      this.handleSave(false, false, true)
    },
    onCreateSummarizeClick() {
      // Force AI enhancements
      this.handleSave(true)
    },
    initializeForm() {
      if (this.card) {
        // Editing existing card
        this.cardForm = {
          id: this.card.id || '',
          title: this.card.title || '',
          content_type: this.card.content_type || 'text',
          content: this.card.content || '',
          fileList: this.card.fileList || [],
          image_description: this.card.image_description || '',
          quiz_content: {
            question: this.card.quiz_content && this.card.quiz_content.question ? this.card.quiz_content.question : '',
            options: this.card.quiz_content && this.card.quiz_content.options ? this.card.quiz_content.options : ['', '', '', ''],
            explanation: this.card.quiz_content && this.card.quiz_content.explanation ? this.card.quiz_content.explanation : ''
          },
          labels: this.card.labels || [],
          enable_reminder: this.card.enable_reminder || false,
          collection_ids: this.card.collection_ids || (this.card.collection_id ? [this.card.collection_id] : []),
          resources: this.card.resources || [],
          reminder_schedule: {
            type: this.card.reminder_schedule && this.card.reminder_schedule.type ? this.card.reminder_schedule.type : 'weekly',
            custom_days: this.card.reminder_schedule && this.card.reminder_schedule.custom_days ? this.card.reminder_schedule.custom_days : 7,
            time: this.card.reminder_schedule && this.card.reminder_schedule.time ? new Date(this.card.reminder_schedule.time) : null,
            start_date: this.card.reminder_schedule && this.card.reminder_schedule.start_date ? new Date(this.card.reminder_schedule.start_date) : new Date(),
            end_date: this.card.reminder_schedule && this.card.reminder_schedule.end_date ? new Date(this.card.reminder_schedule.end_date) : null
          }
        }
        
        // Initialize content store with existing card data
        this.contentStore = {
          text: this.card.content || '',
          image: { 
            url: (this.card.content_type === 'image' && this.card.fileList && this.card.fileList.length > 0) ? this.card.fileList[0].url : '',
            fileList: (this.card.content_type === 'image') ? (this.card.fileList || []) : []
          },
          video: { 
            url: (this.card.content_type === 'video' && this.card.fileList && this.card.fileList.length > 0) ? this.card.fileList[0].url : '',
            fileList: (this.card.content_type === 'video') ? (this.card.fileList || []) : []
          },
          quiz: {
            question: (this.card.quiz_content && this.card.quiz_content.question) ? this.card.quiz_content.question : '',
            options: (this.card.quiz_content && this.card.quiz_content.options) ? this.card.quiz_content.options : ['', '', '', ''],
            explanation: (this.card.quiz_content && this.card.quiz_content.explanation) ? this.card.quiz_content.explanation : ''
          }
        }
        
        // Set the current content type as previous
        this.previousContentType = this.card.content_type || 'text'
        
        // Store original content for modification detection
        this.originalContent = this.card.content || ''
        this.originalQuizContent = this.card.quiz_content ? {
          question: this.card.quiz_content.question || '',
          options: this.card.quiz_content.options || ['', '', '', ''],
          explanation: this.card.quiz_content.explanation || ''
        } : null

        // Set editor type based on card metadata or content detection
        if (this.card.editor_type) {
          this.editorType = this.card.editor_type
        } else if (this.card.content_type === 'text' && this.card.content) {
          // Try to detect the editor type from content
          if (this.card.content.includes('<p>') || this.card.content.includes('<div>') || this.card.content.includes('<strong>')) {
            this.editorType = 'richtext'
          } else if (this.card.content.includes('**') || this.card.content.includes('##') || this.card.content.includes('- ')) {
            this.editorType = 'markdown'
          } else {
            this.editorType = 'plain'
          }
        } else {
          this.editorType = 'plain'
        }

        // Set media URLs for display
        if (this.card.content_type === 'image' && this.card.fileList && this.card.fileList.length > 0) {
          this.imageUrl = this.card.fileList[0].url
        }
        if (this.card.content_type === 'video' && this.card.fileList && this.card.fileList.length > 0) {
          this.videoUrl = this.card.fileList[0].url
        }
      } else {
        // Creating new card
        this.resetForm()
      }
      
      // Initialize resources expanded state
      this.resourcesExpanded = new Array(this.cardForm.resources.length).fill(false)
    },

    resetForm() {
      this.cardForm = {
        id: '',
        title: '',
        content_type: 'text',
        content: '',
        fileList: [],
        quiz_content: {
          question: '',
          options: ['', '', '', ''],
          explanation: ''
        },
        labels: [],
        enable_reminder: true,
        collection_ids: this.preselectedCollectionId ? [this.preselectedCollectionId] : [], // Use preselected collection if available
        resources: []
      }
      this.editorType = 'plain'
      this.imageUrl = ''
      this.videoUrl = ''
      this.originalContent = null
      this.originalQuizContent = null
      this.resourcesExpanded = []
      this.showTitleInput = false // Reset title input visibility

      // Reset content store for new card
      this.contentStore = {
        text: '',
        image: { url: '', fileList: [] },
        video: { url: '', fileList: [] },
        quiz: {
          question: '',
          options: ['', '', '', ''],
          explanation: ''
        }
      }
      this.previousContentType = 'text'
    },

    handleContentTypeChangeWrapper(newType) {
      // Save current content before switching
      if (this.previousContentType && this.previousContentType !== newType) {
        this.saveCurrentContent(this.previousContentType)
      }
      
      // Restore content for the new type
      this.restoreContent(newType)
      
      // Update the previous content type
      this.previousContentType = newType
      
      // Clear validation errors when switching content types
      this.$nextTick(() => {
        this.$refs.cardForm.clearValidate(['fileList', 'content'])
      })
    },

    saveCurrentContent(type) {
      // Save content from the current type before switching
      if (type === 'text') {
        this.contentStore.text = this.cardForm.content
      } else if (type === 'image') {
        this.contentStore.image = {
          url: this.imageUrl,
          fileList: this.cardForm.fileList
        }
      } else if (type === 'video') {
        this.contentStore.video = {
          url: this.videoUrl,
          fileList: this.cardForm.fileList
        }
      } else if (type === 'quiz') {
        this.contentStore.quiz = {
          ...this.cardForm.quiz_content
        }
      }
    },
    
    restoreContent(type) {
      // Restore content for the selected type
      if (type === 'text') {
        this.cardForm.content = this.contentStore.text
      } else if (type === 'image') {
        this.imageUrl = this.contentStore.image.url
        this.cardForm.fileList = this.contentStore.image.fileList
      } else if (type === 'video') {
        this.videoUrl = this.contentStore.video.url
        this.cardForm.fileList = this.contentStore.video.fileList
      } else if (type === 'quiz') {
        this.cardForm.quiz_content = {
          ...this.contentStore.quiz
        }
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async handleImageChange(event) {
      const rawFile = event.target.files[0];
      if (!rawFile) return;

      // Validate file
      const isImage = rawFile.type.startsWith('image/')
      const isLt5M = rawFile.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('Only image files are allowed!')
        return
      }
      if (!isLt5M) {
        this.$message.error('Image size must be smaller than 5MB!')
        return
      }

      try {
        // Show preview immediately using FileReader
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result; // Set temporary local preview
        };
        reader.readAsDataURL(rawFile);

        // Set temporary fileList immediately to satisfy validation
        this.cardForm.fileList = [{
          url: 'uploading', // Temporary placeholder
          mime_type: rawFile.type,
          uploading: true
        }];

        // Get upload token
        const tokenResponse = await postRequest("getSingaporeQiniuToken", {});
        let token = tokenResponse.data.data;

        // Let Qiniu auto-generate hash-based key
        let qiniuKey = null;
        const putExtra = {
          fname: rawFile.name,
          mimeType: rawFile.type
        };
        const config = {
          useCdnDomain: true
        };

        // Upload to Qiniu
        let observable = qiniu.upload(
          rawFile,
          qiniuKey,
          token,
          putExtra,
          config
        );

        observable.subscribe({
          complete: (res) => {
            let url = "https://ap.knowit.today/" + res.key;
            this.imageUrl = url; // Update with actual uploaded URL
            console.log('url:' + url)
            this.cardForm.fileList = [{
              url: url,
              mime_type: rawFile.type,
              uploading: false
            }];
            this.$message.success("Image uploaded successfully");
            // Trigger validation for fileList field
            this.$refs.cardForm.validateField('fileList');
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload image");
            this.imageUrl = ""; // Clear preview on error
            this.cardForm.fileList = []; // Clear fileList on error
          },
        });
      } catch (error) {
        this.$message.error("Failed to get upload token");
        console.error("Error uploading image:", error);
        this.imageUrl = ""; // Clear preview on error
        this.cardForm.fileList = []; // Clear fileList on error
      }
    },


    beforeVideoUpload(file) {
      const isVideo = file.type.startsWith('video/')
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isVideo) {
        this.$message.error('Only video files are allowed!')
        return false
      }
      if (!isLt50M) {
        this.$message.error('Video size must be smaller than 50MB!')
        return false
      }
      return true
    },

    handleVideoSuccess(response, file) {
      this.videoUrl = URL.createObjectURL(file.raw)
      this.cardForm.fileList = [{
        url: (response.data && response.data.url) || this.videoUrl,
        mime_type: file.type
      }]
    },

    async handleMavonImageAdd(pos, file) {
      console.log('[EditCard] Mavon editor image add triggered, pos:', pos);

      try {
        // Show uploading message
        this.$message.info('Uploading image...');

        // Upload to Qiniu
        const cloudUrl = await this.uploadImageToQiniu(file, 'card_image/');

        // Insert the cloud URL into the markdown editor at the specified position
        // mavon-editor's $img2Url method expects (pos, url)
        this.$refs.mavonEditor && this.$refs.mavonEditor.$img2Url(pos, cloudUrl);

        this.$message.success('Image uploaded successfully');
        console.log('[EditCard] Image uploaded to:', cloudUrl);
      } catch (error) {
        console.error('[EditCard] Failed to upload image:', error);
        this.$message.error('Failed to upload image');
      }
    },

    async processInlineImages(markdownContent) {
      // Regular expression to find markdown images: ![alt](url)
      const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
      const matches = [...markdownContent.matchAll(imageRegex)];

      if (matches.length === 0) {
        return markdownContent;
      }

      console.log(`[EditCard] Found ${matches.length} inline images to process`);
      let processedContent = markdownContent;

      for (const match of matches) {
        const fullMatch = match[0]; // Full markdown image syntax
        const altText = match[1]; // Alt text
        const imageUrl = match[2]; // Image URL

        // Skip if already a cloud URL (ap.knowit.today or other http/https URLs)
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
          console.log(`[EditCard] Skipping already uploaded image: ${imageUrl}`);
          continue;
        }

        // Handle data URLs (base64 images)
        if (imageUrl.startsWith('data:image/')) {
          try {
            console.log(`[EditCard] Uploading base64 image...`);

            // Convert data URL to blob
            const blob = await this.dataURLtoBlob(imageUrl);

            // Upload to Qiniu
            const cloudUrl = await this.uploadImageToQiniu(blob, 'card_image/');

            // Replace the data URL with cloud URL in markdown
            const newImageMarkdown = `![${altText}](${cloudUrl})`;
            processedContent = processedContent.replace(fullMatch, newImageMarkdown);

            console.log(`[EditCard] Uploaded base64 image to: ${cloudUrl}`);
          } catch (error) {
            console.error(`[EditCard] Failed to upload base64 image:`, error);
            throw error;
          }
        }
        // Handle blob URLs or local file paths
        else if (imageUrl.startsWith('blob:') || !imageUrl.includes('://')) {
          console.log(`[EditCard] Warning: Found local/blob image that cannot be uploaded: ${imageUrl}`);
          // Note: blob URLs cannot be uploaded as they are temporary browser references
          // Users should copy/paste images directly which will create data URLs
        }
      }

      return processedContent;
    },

    dataURLtoBlob(dataURL) {
      return new Promise((resolve, reject) => {
        try {
          const arr = dataURL.split(',');
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          resolve(new Blob([u8arr], { type: mime }));
        } catch (error) {
          reject(error);
        }
      });
    },

    uploadImageToQiniu(blob, prefix = 'card_image/') {
      return new Promise(async (resolve, reject) => {
        try {
          // Get upload token
          const tokenResponse = await postRequest("getSingaporeQiniuToken", {});
          const token = tokenResponse.data.data;

          // Generate unique key with prefix
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(2, 15);
          const qiniuKey = `${prefix}${timestamp}_${randomStr}`;

          const putExtra = {
            fname: `inline_${timestamp}.png`,
            mimeType: blob.type || 'image/png'
          };
          const config = {
            useCdnDomain: true
          };

          // Upload to Qiniu
          const observable = qiniu.upload(blob, qiniuKey, token, putExtra, config);

          observable.subscribe({
            complete: (res) => {
              const url = "https://ap.knowit.today/" + res.key;
              console.log('[EditCard] Qiniu upload complete with prefix:', url);
              resolve(url);
            },
            error: (err) => {
              console.error("[EditCard] Qiniu upload error:", err);
              reject(err);
            }
          });
        } catch (error) {
          console.error("[EditCard] Failed to get upload token:", error);
          reject(error);
        }
      });
    },

    async handleSaveWithEnhancement() {
      console.log('[EditCard] handleSaveWithEnhancement called')
      // Call handleSave with forceEnhancement flag
      await this.handleSave(false, true)
    },

    async handleSave(shouldSummarize = false, forceEnhancement = false, primitiveCreate = false) {
      console.log('[EditCard] handleSave called. summarize =', shouldSummarize, 'forceEnhancement =', forceEnhancement, 'primitiveCreate =', primitiveCreate)
      // Prevent double submission
      if (this.saving || this.savingAndSummarizing || this.savingWithEnhancement) {
        console.warn('[EditCard] Blocked: already saving. saving=', this.saving, 'savingAndSummarizing=', this.savingAndSummarizing, 'savingWithEnhancement=', this.savingWithEnhancement)
        return
      }

      this.$refs.cardForm.validate(async (valid) => {
        console.log('[EditCard] form validate result =', valid)
        if (!valid) return

        // Additional validation for quiz
        if (this.cardForm.content_type === 'quiz') {
          const filledOptions = this.cardForm.quiz_content.options.filter(opt => opt.trim() !== '')
          if (filledOptions.length < 2) {
            this.$message.error('Please provide at least 2 quiz options')
            return
          }
        }

        // Additional validation for media
        if ((this.cardForm.content_type === 'image' || this.cardForm.content_type === 'video') &&
          (!this.cardForm.fileList || this.cardForm.fileList.length === 0)) {
          this.$message.error(`Please upload a ${this.cardForm.content_type}`)
          return
        }

        // Check if image is still uploading
        if (this.cardForm.content_type === 'image' && this.cardForm.fileList &&
          this.cardForm.fileList.length > 0 && this.cardForm.fileList[0].uploading) {
          this.$message.warning('Please wait for the image to finish uploading')
          return
        }

        // Additional validation for primitive create (without AI)
        if (primitiveCreate && !this.isEditing) {
          if (!this.cardForm.title || this.cardForm.title.trim() === '') {
            this.$message.error('Please enter a card title when creating without AI enhancement')
            return
          }
        }

        if (shouldSummarize) {
          this.savingAndSummarizing = true
        } else if (forceEnhancement) {
          this.savingWithEnhancement = true
        } else {
          this.saving = true
        }

        // Process inline images in markdown content if markdown editor is used
        if (this.cardForm.content_type === 'text' && this.editorType === 'markdown') {
          try {
            this.cardForm.content = await this.processInlineImages(this.cardForm.content)
          } catch (error) {
            console.error('[EditCard] Error processing inline images:', error)
            this.$message.error('Failed to upload inline images')
            this.saving = false
            this.savingAndSummarizing = false
            this.savingWithEnhancement = false
            return
          }
        }

        // Skip AI enhancement when editing existing cards, or when primitive create is requested
        const skipAIEnhancement = (this.isEditing && !forceEnhancement) || primitiveCreate
        if (skipAIEnhancement && primitiveCreate) {
          console.log('[EditCard] Primitive create: skipping all AI enhancements')
        } else if (skipAIEnhancement) {
          console.log('[EditCard] Skipping AI enhancement for existing card update')
        } else if (forceEnhancement) {
          console.log('[EditCard] Force AI enhancement for existing card update')
        }
        
        // Check if title is blank and handle based on AI enhancement setting
        let finalTitle = this.cardForm.title
        if (!skipAIEnhancement && (!finalTitle || finalTitle.trim() === '')) {
          console.log('[EditCard] Title empty. Generating with AI...')
          try {
              // Prepare content for AI title generation
              let contentForAI = ''
              if (this.cardForm.content_type === 'text') {
                contentForAI = this.cardForm.content
              } else if (this.cardForm.content_type === 'quiz') {
                contentForAI = `Quiz Question: ${this.cardForm.quiz_content.question}\nOptions: ${this.cardForm.quiz_content.options.filter(opt => opt.trim()).join(', ')}\nExplanation: ${this.cardForm.quiz_content.explanation}`
              } else if (this.cardForm.content_type === 'image') {
                contentForAI = 'Image card'
              } else if (this.cardForm.content_type === 'video') {
                contentForAI = 'Video card'
              }
              
              // Create prompt for AI title generation
              const prompt = `Generate a concise and descriptive title for the following card content. The title should be clear, informative, and no longer than 100 characters. 

IMPORTANT: Return ONLY the title text itself. Do NOT include:
- Any prefixes like "Title:", "Card Title:", etc.
- Any quotation marks or other punctuation around the title
- Any explanatory text before or after the title
- Any metadata or formatting markers

Just return the plain title text and nothing else.

Card Content:
${contentForAI}`
              
              this.$message.info('Generating title with AI...')
              console.log('[EditCard] AI title prompt =', prompt)
              
              // Call AI service to generate title
              const response = await postRequest('exeModelGeneral', {
                content: prompt,
                model: 'siliconflow/deepseek-ai/DeepSeek-V3' // Using DeepSeek-V3 for title generation
              })
              
              if (response && response.data && response.data.code === 0 && response.data.data) {
                console.log('[EditCard] AI title response OK')
                finalTitle = response.data.data.trim()
                // Ensure title is not too long
                if (finalTitle.length > 100) {
                  finalTitle = finalTitle.substring(0, 97) + '...'
                }
                this.$message.success('Title generated successfully')
              } else {
                console.warn('[EditCard] AI title response not usable, fallback to default')
                // If AI generation fails, use a default title
                finalTitle = `Card created on ${new Date().toLocaleDateString()}`
                this.$message.warning('Could not generate title with AI, using default')
              }
            } catch (error) {
            console.error('[EditCard] Error generating title with AI:', error)
            // Fallback to default title if AI fails
            finalTitle = `Card created on ${new Date().toLocaleDateString()}`
            this.$message.warning('Could not generate title with AI, using default')
          }
        } else if (primitiveCreate && (!finalTitle || finalTitle.trim() === '')) {
          // For primitive create, use default title if blank
          finalTitle = `Card created on ${new Date().toLocaleDateString()}`
        } else if(this.cardForm.content_type === 'image' && (!finalTitle || finalTitle.trim() === '')) {
          try {
            this.$message.info('Generating title with AI for image...')
            if(this.cardForm.content_type === 'image') {
              const response = await sendConversation({
                model: 'gpt-4o-mini',
                messages: [{
                  role: "user",
                  content: [
                        { type: "text", text: `Generate a concise and descriptive title for the following image. The title should be clear, informative, and no longer than 100 characters. Only return the title text, nothing else.` },
                        {
                          type: "image_url",
                          image_url: { url: this.imageUrl },
                        },
                  ]
                }]
              })
              if(response && response.data && response.data.code === 0 && response.data.data) {
                finalTitle = response.data.data.choices[0].message.content
                this.$message.success('Title generated successfully')
              } else {
                finalTitle = `Card created on ${new Date().toLocaleDateString()}`
                this.$message.warning('Could not generate title with AI, using default')
              }
            }
          } catch (error) {
            console.error('[EditCard] Error generating image title with AI:', error)
            finalTitle = `Card created on ${new Date().toLocaleDateString()}`
            this.$message.warning('Could not generate title with AI, using default')
          }
        }
        
        // Store original content before any AI processing
        const originalScriptBeforeAI = this.cardForm.content_type === 'text' ? this.cardForm.content :
                                      this.cardForm.content_type === 'quiz' ?
                                      `Quiz Question: ${this.cardForm.quiz_content.question}\nOptions: ${this.cardForm.quiz_content.options.filter(opt => opt.trim()).join(', ')}\nExplanation: ${this.cardForm.quiz_content.explanation}` : ''

        // Fix grammar and typos in content using AI (for text and quiz content)
        // Only process if AI enhancements are enabled and content has been modified
        let finalContent = this.cardForm.content
        let finalQuizContent = { ...this.cardForm.quiz_content }

        // Check if text content has been modified
        const textContentModified = this.isEditing ?
          (this.originalContent !== this.cardForm.content) :
          true // Always check for new cards

        if (!skipAIEnhancement && this.cardForm.content_type === 'text' && finalContent && finalContent.trim()) {
          console.log('[EditCard] Processing TEXT content. summarize =', shouldSummarize, 'forceEnhancement =', forceEnhancement)
          // Process content with AI based on which button was clicked
          // Force enhancement bypasses the modification check
          if (textContentModified || forceEnhancement) {
            try {
            // Different prompts based on button clicked
            let contentPrompt;
            if (shouldSummarize) {
              // "Create and Summarize" button - summarize the content
              contentPrompt = `Summarize the following text into concise bullet points or key takeaways. Keep the most important information and make it easy to review. Return only the summarized content without any additional commentary.\n\nText to summarize:\n${finalContent}`
              this.$message.info('Summarizing content...')
            } else {
              // "Create" button - enhance the content (fix grammar, improve clarity)
              contentPrompt = `Please enhance the following text by:
1. Correcting all spelling, grammar, and punctuation errors
2. Improving clarity and flow
3. Maintaining the original meaning and length

<IMPORTANT>
<MUST FOLLOW THESE RULES!!!>
- DETECT the original language of the text (NOT necessarily the same as the language of the PROMPT template).The enhancement MUST keep the original language of the text. DO NOT translate the text into another language.
</MUST FOLLOW THESE RULES!!!>
- Do NOT summarize or shorten the content. Only fix errors and improve readability. 
- MUST Return the enhanced text ONLY, WIHTOUT any extra introduction, opening, closing, explanatory texts BEFORE or AFTER the enhanced text itself.
- DO NOT / MUST NOT add ANY additional commentary, explanation, or formatting to the enhanced text.
- DO NOT / MUST NOT add introductory texts like "Here is/are the XXXX..." and ending texts like "Please see if ......". NO interaction texts with the user should be added!!!!.
- ONLY return the enhanced text itself!
</IMPORTANT>

Text to enhance:<ORIGINAL TEXT - PLEASE DETECT THE LANGUAGE OF THE TEXT>
${finalContent}</ORIGINAL TEXT>`
              this.$message.info('Enhancing content...')
            }

            // Call AI service to process content
            console.log('[EditCard] AI content prompt =', contentPrompt)
            const contentResponse = await postRequest('exeModelGeneral', {
              content: contentPrompt,
              model: 'siliconflow/deepseek-ai/DeepSeek-V3' // Using DeepSeek-V3
            })

            if (contentResponse && contentResponse.data && contentResponse.data.code === 0 && contentResponse.data.data) {
              console.log('[EditCard] AI content response OK')
              const processedContent = contentResponse.data.data.trim()
              
              // Only update if there are actual changes
              if (processedContent !== finalContent) {
                finalContent = processedContent
                // Update the form with processed content for label generation
                this.cardForm.content = processedContent
                this.$message.success(shouldSummarize ? 'Content has been summarized' : 'Content has been enhanced')
              }
            } else {
              this.$message.warning(shouldSummarize ? 'Could not summarize content with AI' : 'Could not enhance content with AI')
            }
            } catch (error) {
              console.error('[EditCard]', shouldSummarize ? 'Error summarizing content:' : 'Error enhancing content:', error)
              this.$message.warning(shouldSummarize ? 'Could not summarize content with AI' : 'Could not enhance content with AI')
            }
          }
        } else if (!skipAIEnhancement && this.cardForm.content_type === 'quiz') {
          console.log('[EditCard] Processing QUIZ content. summarize =', shouldSummarize, 'forceEnhancement =', forceEnhancement)
          // Check if quiz content has been modified
          const quizContentModified = this.isEditing && this.originalQuizContent ?
            (this.originalQuizContent.question !== this.cardForm.quiz_content.question ||
              JSON.stringify(this.originalQuizContent.options) !== JSON.stringify(this.cardForm.quiz_content.options) ||
              this.originalQuizContent.explanation !== this.cardForm.quiz_content.explanation) :
            true // Always check for new cards

          // Process quiz content with AI based on which button was clicked
          // Force enhancement bypasses the modification check
          if (quizContentModified || forceEnhancement) {
            try {
              // Process quiz question based on button clicked
            if (this.cardForm.quiz_content.question && this.cardForm.quiz_content.question.trim()) {
              let questionPrompt;
              if (shouldSummarize) {
                // "Create and Summarize" - make it concise
                questionPrompt = `Summarize and refine the following quiz question into a clear, concise format. Make it easy to review while preserving the core meaning. Return ONLY the refined question.

Quiz question to summarize:
${this.cardForm.quiz_content.question}`
              } else {
                // "Create" - enhance without summarizing
                questionPrompt = `Please enhance the following quiz question by:
1. Correcting all spelling, grammar, and punctuation errors
2. Improving clarity and professional tone
3. Maintaining the original length and detail

IMPORTANT: Do NOT summarize or shorten. Only fix errors and improve clarity. Return ONLY the enhanced question.

Quiz question to enhance:
${this.cardForm.quiz_content.question}`
              }

              this.$message.info(shouldSummarize ? 'Summarizing quiz content...' : 'Enhancing quiz content...')

              console.log('[EditCard] AI quiz question prompt =', questionPrompt)
              const questionResponse = await postRequest('exeModelGeneral', {
                content: questionPrompt,
                model: 'siliconflow/deepseek-ai/DeepSeek-V3'
              })
              
              if (questionResponse && questionResponse.data && questionResponse.data.code === 0 && questionResponse.data.data) {
                const correctedQuestion = questionResponse.data.data.trim()
                if (correctedQuestion !== this.cardForm.quiz_content.question) {
                  finalQuizContent.question = correctedQuestion
                  this.cardForm.quiz_content.question = correctedQuestion
                }
              }
            }
            
            // Process quiz options based on button clicked
            for (let i = 0; i < this.cardForm.quiz_content.options.length; i++) {
              const option = this.cardForm.quiz_content.options[i]
              if (option && option.trim()) {
                let optionPrompt;
                if (shouldSummarize) {
                  // "Create and Summarize" - make it concise
                  optionPrompt = `Summarize this quiz option into a concise, clear format. Preserve the meaning but make it brief and easy to review. Return ONLY the summarized option.

Option to summarize:
${option}`
                } else {
                  // "Create" - enhance without summarizing
                  optionPrompt = `Please enhance this quiz option by correcting any errors and improving clarity. Do NOT summarize or shorten. Return ONLY the enhanced option.

Option to enhance:
${option}`
                }

                console.log('[EditCard] AI quiz option prompt =', optionPrompt)
                const optionResponse = await postRequest('exeModelGeneral', {
                  content: optionPrompt,
                  model: 'siliconflow/deepseek-ai/DeepSeek-V3'
                })
                
                if (optionResponse && optionResponse.data && optionResponse.data.code === 0 && optionResponse.data.data) {
                  const correctedOption = optionResponse.data.data.trim()
                  if (correctedOption !== option) {
                    finalQuizContent.options[i] = correctedOption
                    this.cardForm.quiz_content.options[i] = correctedOption
                  }
                }
              }
            }


            // Process quiz explanation based on button clicked
            if (this.cardForm.quiz_content.explanation && this.cardForm.quiz_content.explanation.trim()) {
              let explanationPrompt;
              if (shouldSummarize) {
                // "Create and Summarize" - make it concise
                explanationPrompt = `Summarize this quiz explanation into concise, key points that are easy to review. Keep the most important information while making it brief and clear. Return ONLY the summarized explanation.

Explanation to summarize:
${this.cardForm.quiz_content.explanation}`
              } else {
                // "Create" - enhance without summarizing
                explanationPrompt = `Please enhance this quiz explanation by:
1. Correcting all spelling, grammar, and punctuation errors
2. Improving clarity and flow
3. Maintaining the original detail and length

IMPORTANT: Do NOT summarize or shorten. Only fix errors and improve readability. Return ONLY the enhanced explanation.

Explanation to enhance:
${this.cardForm.quiz_content.explanation}`
              }

              console.log('[EditCard] AI quiz explanation prompt =', explanationPrompt)
              const explanationResponse = await postRequest('exeModelGeneral', {
                content: explanationPrompt,
                model: 'siliconflow/deepseek-ai/DeepSeek-V3'
              })
              
              if (explanationResponse && explanationResponse.data && explanationResponse.data.code === 0 && explanationResponse.data.data) {
                const correctedExplanation = explanationResponse.data.data.trim()
                if (correctedExplanation !== this.cardForm.quiz_content.explanation) {
                  finalQuizContent.explanation = correctedExplanation
                  this.cardForm.quiz_content.explanation = correctedExplanation
                }
              }
            }
            
              this.$message.success(shouldSummarize ? 'Quiz content has been summarized' : 'Quiz content has been enhanced')
            } catch (error) {
              console.error('[EditCard]', shouldSummarize ? 'Error summarizing quiz content:' : 'Error enhancing quiz content:', error)
              this.$message.warning(shouldSummarize ? 'Could not summarize quiz content with AI' : 'Could not enhance quiz content with AI')
            }
          }
        }
        
        // Generate labels using AI (only when AI enhancements are enabled and for new cards or cards without labels)
        let finalLabels = this.cardForm.labels || []
        if (!skipAIEnhancement && (!this.isEditing || finalLabels.length === 0) && this.cardForm.content_type !== 'image' && this.cardForm.content_type !== 'video') {
          try {
          console.log('[EditCard] Generating labels with AI...')
          // Prepare content for AI label generation
          let contentForLabels = ''
          if (this.cardForm.content_type === 'text') {
            contentForLabels = this.cardForm.content
          } else if (this.cardForm.content_type === 'quiz') {
            contentForLabels = `Quiz Question: ${this.cardForm.quiz_content.question}\nOptions: ${this.cardForm.quiz_content.options.filter(opt => opt.trim()).join(', ')}\nExplanation: ${this.cardForm.quiz_content.explanation}`
          } else if (this.cardForm.content_type === 'image') {
            contentForLabels = 'Image card with title: ' + finalTitle
          } else if (this.cardForm.content_type === 'video') {
            contentForLabels = 'Video card with title: ' + finalTitle
          }
          
          // Create prompt for AI label generation
          const labelPrompt = `Analyze the following card content and generate 2-5 relevant labels/tags that categorize this content. Labels should be concise (1-3 words each), descriptive, and help with organizing and finding this card later. Return ONLY a clean JSON array of strings, nothing else.

Example output: ["JavaScript", "Tutorial", "Async Programming"]

Card Title: ${finalTitle}
Card Content:
${contentForLabels}`
          
          this.$message.info('Generating labels with AI...')
          
          // Call AI service to generate labels
          console.log('[EditCard] AI label prompt =', labelPrompt)
          const labelResponse = await postRequest('exeModelGeneral', {
            content: labelPrompt,
            model: 'siliconflow/deepseek-ai/DeepSeek-V3' // Using DeepSeek-V3 for label generation
          })
          
          if (labelResponse && labelResponse.data && labelResponse.data.code === 0 && labelResponse.data.data) {
            try {
              // Parse the JSON array from AI response
              const rawResponse = labelResponse.data.data.trim()
              // Extract JSON array even if AI adds extra text
              const jsonMatch = rawResponse.match(/\[.*\]/s)
              if (jsonMatch) {
                finalLabels = JSON.parse(jsonMatch[0])
                // Validate that we got an array of strings
                if (Array.isArray(finalLabels) && finalLabels.every(label => typeof label === 'string')) {
                  // Clean up labels - trim and limit length
                  finalLabels = finalLabels
                    .map(label => label.trim())
                    .filter(label => label.length > 0 && label.length <= 50)
                    .slice(0, 5) // Maximum 5 labels
                  this.$message.success(`Generated ${finalLabels.length} labels successfully`)
                } else {
                  finalLabels = []
                  console.warn('AI returned invalid label format')
                }
              } else {
                finalLabels = []
                console.warn('Could not extract JSON array from AI response')
              }
            } catch (parseError) {
              console.error('Error parsing AI label response:', parseError)
              finalLabels = []
            }
          }
          } catch (error) {
            console.error('Error generating labels with AI:', error)
            this.$message.warning('Could not generate labels with AI')
          }
        }

        let imageDescription = ''
        if(this.cardForm.content_type === 'image') {
          const response = await sendConversation({
                model: 'gpt-4o-mini',
                messages: [{
                  role: "user",
                  content: [
                        { type: "text", text: `Please describe the image in detail. Return the description directly and only,no extra words.` },
                        {
                          type: "image_url",
                          image_url: { url: this.imageUrl },
                        },
                  ]
                }]
              })
              if(response && response.data && response.data.code === 0 && response.data.data) {
                imageDescription = response.data.data.choices[0].message.content
              }
        }

        // Prepare card data with the final title, corrected content, and labels
        const cardData = {
          ...this.cardForm,
          title: finalTitle,
          content: this.cardForm.content_type === 'text' ? (finalContent || this.cardForm.content) : this.cardForm.content,
          quiz_content: this.cardForm.content_type === 'quiz' ? finalQuizContent : this.cardForm.quiz_content,
          labels: finalLabels,
          resources: this.cardForm.resources.filter(r => r.name && r.name.trim()), // Only include resources with names
          editor_type: this.editorType,
          original_script: shouldSummarize ? originalScriptBeforeAI : undefined, // Only include if summarizing
          created_at: this.cardForm.id ? this.card.created_at : Date.now(),
          updated_at: Date.now(),
          image_description: imageDescription
        }
        console.log('[EditCard] Prepared cardData for save:', cardData)
        // Ensure id is present for update path
        cardData.id = this.cardForm.id || cardData.id || ''
        console.log('[EditCard] Final card id =', cardData.id)
        
        // Save to backend directly from here
        try {
          console.log('[EditCard] Calling API updateCard... is_edit =', !!(cardData.id && cardData.id !== ''))
          const isEditing = !!(cardData.id && cardData.id !== '')
          const response = await postRequest('updateCard', {
            user_id: this.$store.state.user_id,
            card_data: cardData,
            is_edit: isEditing
          })

          if (response && response.data && response.data.code === 0) {
            console.log('[EditCard] updateCard success. Response:', response.data)

            // Use returned card if provided, otherwise our prepared cardData
            let savedCard = (response.data.data && typeof response.data.data === 'object') ? response.data.data : cardData

            // Ensure ID is preserved when editing
            if (isEditing && cardData.id && (!savedCard.id || savedCard.id === '')) {
              savedCard = { ...savedCard, id: cardData.id }
            }

            console.log('[EditCard] Emitting saved with card:', savedCard)

            // Emit saved event for parent to update UI without full reload
            this.$emit('saved', savedCard)

            if (isEditing) {
              // For editing, show success message and close immediately
              this.$message.success('Card updated successfully')
              this.dialogVisible = false
              this.$emit('close')
            } else {
              // For new card creation, show reminder dialog
              this.$message.success('Card created successfully')
              this.createdCardData = savedCard
              this.showReminderDialog = true
            }
          } else {
            console.error('[EditCard] updateCard failed. Response:', response && response.data)
            this.$message.error((response && response.data && response.data.msg) || 'Failed to save card')
          }
        } catch (error) {
          console.error('[EditCard] Error saving card:', error)
          this.$message.error('Failed to save card')
        } finally {
          // Release local saving flags regardless of outcome
          console.log('[EditCard] Resetting saving flags')
          this.saving = false
          this.savingAndSummarizing = false
          this.savingWithEnhancement = false
        }
      })
    },

    // Deprecated: saving is now handled inside handleSave
    async saveCard(cardData) {
      // Kept for backward compatibility if invoked elsewhere
      this.$emit('saved', cardData)
    },
    
    handleSaveAndSummarize() {
      this.handleSave(true) // Pass true to indicate summarization
    },


    resetSavingState() {
      // Method to reset saving state, can be called by parent
      this.saving = false
      this.savingAndSummarizing = false
      this.savingWithEnhancement = false
    },

    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
      // Clean up any lingering modal backdrops
      this.$nextTick(() => {
        const modals = document.querySelectorAll('.v-modal')
        modals.forEach(modal => {
          if (modal && modal.style) {
            modal.style.display = 'none'
          }
        })
      })
    },

    async handleReminderChoice(enableReminders) {
      this.showReminderDialog = false

      if (enableReminders && this.createdCardData) {
        try {
          // Create reminder schedule for the new card
          const response = await postRequest('createCardReminderSchedule', {
            user_id: this.$store.state.user_id,
            card_id: this.createdCardData.id,
            enable_reminder: true
          })

          if (response && response.data && response.data.code === 0) {
            this.$message.success('Reminders enabled successfully!')
          } else {
            this.$message.error('Failed to enable reminders')
          }
        } catch (error) {
          console.error('Error creating reminder schedule:', error)
          this.$message.error('Failed to enable reminders')
        }
      }

      // Close the main dialog and clean up
      this.dialogVisible = false
      this.$emit('close')
      this.createdCardData = null
    },

    // Method to add transcribed text to content field
    addTextToContent(text) {
      if (this.cardForm.content) {
        // Append to existing content with a space
        this.cardForm.content = this.cardForm.content + ' ' + text;
      } else {
        // Set as new content
        this.cardForm.content = text;
      }
    },

    // Method to add transcribed text to quiz question
    addTextToQuizQuestion(text) {
      if (this.cardForm.quiz_content && this.cardForm.quiz_content.question) {
        // Append to existing question with a space
        this.cardForm.quiz_content.question = this.cardForm.quiz_content.question + ' ' + text;
      } else if (this.cardForm.quiz_content) {
        // Set as new question
        this.cardForm.quiz_content.question = text;
      }
    },
    
    // Resource management methods
    addResource() {
      this.cardForm.resources.push({
        name: '',
        url: '',
        type: '',
        author: '',
        major: '',
        subject: '',
        description: ''
      })
      this.resourcesExpanded.push(false)
    },
    
    removeResource(index) {
      this.cardForm.resources.splice(index, 1)
      this.resourcesExpanded.splice(index, 1)
    },
    
    toggleResourceExpand(index) {
      this.$set(this.resourcesExpanded, index, !this.resourcesExpanded[index])
    }
  }
}
</script>

<style scoped>
/* Custom Dialog Header */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

.dialog-header-buttons {
  display: flex;
  gap: 10px;
}

/* Create button wrapper for dropdown */
.create-button-wrapper {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.update-button-wrapper {
  position: relative;
  display: inline-block;
}

.enhance-button-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  padding-top: 8px;
  z-index: 10;
}

.enhance-button {
  white-space: nowrap;
  animation: slideDown 0.2s ease-out;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Create button specific styles */
.create-button {
  display: block;
}

/* Summarize button dropdown styles */
.summarize-button {
  margin-top: 8px;
  white-space: nowrap;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  display: block;
  width: 100%;
}

/* Slide down transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}

/* No Label Form Styles */
.no-label-form :deep(.el-form-item) {
  margin-bottom: 22px;
}

.form-item-no-label :deep(.el-form-item__content) {
  margin-left: 0 !important;
}

/* Larger Input Fields */
.form-item-no-label :deep(.el-input__inner) {
  height: 42px;
  font-size: 14px;
}

.form-item-no-label :deep(.el-textarea__inner) {
  font-size: 14px;
  padding: 10px 15px;
}

/* Radio Button Groups */
.form-item-no-label :deep(.el-radio-group) {
  margin-bottom: 10px;
}

.form-item-no-label :deep(.el-radio-button__inner) {
  padding: 10px 20px;
  font-size: 14px;
}

/* Reminder Item Special Styling */
.reminder-item {
  display: flex;
  align-items: center;
}

.reminder-info-icon {
  margin-left: 10px;
  color: #909399;
  cursor: help;
}

.quiz-option-input {
  margin-bottom: 12px;
}

/* Image Upload Styles */
.image-upload-container {
  display: inline-block;
}

.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background-color: #fafafa;
}

.upload-area:hover {
  border-color: #409EFF;
  background-color: #f5f7fa;
}

.upload-area.has-image {
  border: 2px solid #e4e7ed;
  background-color: transparent;
}

.upload-area.has-image:hover {
  border-color: #409EFF;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #8c939d;
}

.upload-icon {
  font-size: 32px;
  margin-bottom: 8px;
  color: #c0c4cc;
}

.upload-text {
  margin: 0;
  font-size: 14px;
  color: #606266;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.image-uploader,
.video-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-uploader:hover,
.video-uploader:hover {
  border-color: #409EFF;
}

.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}

.uploaded-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c939d;
}

.upload-placeholder i {
  font-size: 28px;
  margin-bottom: 8px;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
}

.dialog-footer {
  text-align: right;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-textarea__inner) {
  border-radius: 4px;
  word-break: keep-all;
  overflow-wrap: break-word;
}

:deep(.el-input__count) {
  position: relative !important;
  bottom: auto !important;
  right: auto !important;
  margin-top: 5px;
  display: block;
  text-align: right;
}

:deep(.el-input-group__prepend) {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {

  .upload-area,
  .image-uploader,
  .video-uploader {
    width: 150px;
    height: 150px;
  }

  .upload-icon {
    font-size: 28px;
  }

  .upload-text {
    font-size: 12px;
  }

  .upload-placeholder i {
    font-size: 24px;
  }
}

/* Add Title Manually Button Styles */
.add-title-button-wrapper {
  margin-bottom: 20px;
}

.add-title-button-wrapper .el-button {
  font-size: 13px;
}

/* Fix for modal overlay blocking issue */
:deep(.el-dialog__wrapper) {
  z-index: 2000 !important;
  pointer-events: auto !important;
}

:deep(.v-modal) {
  z-index: 1999 !important;
  pointer-events: auto !important;
}

:deep(.el-dialog) {
  z-index: 2001 !important;
}

/* Ensure the modal backdrop doesn't persist when hidden */
:deep(.el-dialog__wrapper[aria-hidden="true"]) {
  display: none !important;
}

/* Audio Recording Wrapper Styles */
.audio-recording-wrapper {
  margin-bottom: 10px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.audio-recording-wrapper :deep(.translate-container) {
  margin: 0;
}

/* Audio Recording Inline Styles - for single line below textarea */
.audio-recording-inline {
  margin-top: 8px;
  display: flex;
  align-items: center;
  height: 32px;
}

.audio-recording-inline :deep(.translate-container) {
  margin: 0;
  display: flex;
  align-items: center;
}

.audio-recording-inline :deep(.el-button) {
  height: 28px;
  padding: 0 15px;
  font-size: 13px;
}

/* Fix word wrapping in Content textarea */
.content-textarea-item :deep(.el-textarea__inner) {
  word-wrap: break-word;
  word-break: normal;
  white-space: pre-wrap;
}

/* Move word count to same row as audio recording */
.content-textarea-item :deep(.el-input__count) {
  position: relative;
  bottom: auto;
  right: auto;
  float: right;
  margin-top: 8px;
  text-align: right;
  background: transparent;
  line-height: 32px;
}

/* Position audio recording inline container to hold both audio and word count */
.content-textarea-item {
  position: relative;
}

.content-textarea-item .audio-recording-inline {
  position: relative;
  margin-top: 8px;
  float: left;
}

/* Clear float after the row */
.content-textarea-item::after {
  content: "";
  display: table;
  clear: both;
}

/* Markdown Editor Styles */
.markdown-editor-item :deep(.v-note-wrapper) {
  z-index: 2010;
  min-height: 400px;
  max-height: 600px;
}

.markdown-editor-item :deep(.v-note-wrapper .v-note-panel) {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.markdown-editor-item :deep(.v-note-wrapper .v-note-edit.divarea-wrapper) {
  overflow-y: auto;
  max-height: 550px;
}

.markdown-editor-item :deep(.v-note-wrapper .v-show-content) {
  overflow-y: auto;
  max-height: 550px;
  padding: 10px;
}

/* Rich Text Editor Styles */
.richtext-editor-item :deep(.quill-editor) {
  min-height: 400px;
  max-height: 600px;
}

.richtext-editor-item :deep(.ql-container) {
  min-height: 350px;
  max-height: 550px;
  overflow-y: auto;
  font-size: 14px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.richtext-editor-item :deep(.ql-toolbar) {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.richtext-editor-item :deep(.ql-snow .ql-picker) {
  line-height: 22px;
}

/* Editor Tips */
.editor-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* Ensure editor dropdowns appear above dialog */
:deep(.ql-picker-options) {
  z-index: 2100 !important;
}

:deep(.v-note-wrapper .v-note-op) {
  z-index: 2011 !important;
}

:deep(.auto-textarea-wrapper) {
  z-index: 2012 !important;
}
/* Resources Section Styles */
.resources-section {
  margin-top: 20px;
  margin-bottom: 20px;
}

.resources-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.resources-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.resources-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.resources-hint {
  font-size: 13px;
  color: #909399;
  font-style: italic;
}

.resources-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 5px;
}

.resource-item {
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.3s ease;
}

.resource-item.expanded {
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.resource-quick-input {
  display: flex;
  align-items: center;
  gap: 10px;
}

.resource-name-input {
  flex: 1;
}

.resource-actions {
  display: flex;
  gap: 5px;
}

.resource-actions .el-button {
  padding: 7px;
}

.resource-details {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.resource-details .el-input__inner,
.resource-details .el-textarea__inner {
  font-size: 13px;
}

.resource-details .el-input-group__prepend {
  padding: 0 10px;
  font-size: 12px;
}

</style>